import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import React, { useContext, useState } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import { UserContext } from '../../../contexts/userContext';
import routes from '../../../Resources/routes.json';
import { useNavigate } from 'react-router-dom';
import { UserType } from '../../../interfaces/UserTypes';

interface InformationSalidomoProps {
  values: I_Installation;
  s3Credentials: I_S3Credentials;
  type?: string;
}

function InformationSalidomo(props: InformationSalidomoProps) {
  if (props.values === null) {
    return null;
  }

  const context = useContext(UserContext);
  const { currentUser } = context;
  const theme = useTheme();
  const [formValues, setFormValues] = useState(props.values);
  const requiredFields = ['name', 'region', 'location', 'country'];
  const [openModalDeleteInstallation, setOpenModalDeleteInstallation] =
    useState(false);
  const navigate = useNavigate();

  const DeviceTypes = ['Cerbo', 'Venus'];

  const installationContext = useContext(InstallationsContext);
  const {
    updateInstallation,
    deleteInstallation,
    loading,
    setLoading,
    error,
    setError,
    updated,
    setUpdated
  } = installationContext;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  const handleSubmit = () => {
    setLoading(true);
    setError(false);
    updateInstallation(formValues, props.type);
  };

  const handleDelete = () => {
    setLoading(true);
    setError(false);
    setOpenModalDeleteInstallation(true);
  };

  const deleteInstallationModalHandle = () => {
    setOpenModalDeleteInstallation(false);
    deleteInstallation(formValues, props.type);
    setLoading(false);

    navigate(routes.salidomo_installations + routes.list, {
      replace: true
    });
  };

  const deleteInstallationModalHandleCancel = () => {
    setOpenModalDeleteInstallation(false);
    setLoading(false);
  };

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    return true;
  };

  return (
    <>
      {openModalDeleteInstallation && (
        <Modal
          open={openModalDeleteInstallation}
          onClose={deleteInstallationModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage
                id="confirmDeleteInstallation"
                defaultMessage="Do you want to delete this installation?"
              />
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                userSelect: 'all',
                width: '100%',
                textAlign: 'center'
              }}
            >
              {props.s3Credentials.s3Bucket}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center' }}
            >
              <FormattedMessage
                id="deleteInstallationWarning"
                defaultMessage="Please note the bucket name above. Purging S3 data may take several minutes. If deletion fails, you can manually remove the bucket from Exoscale."
              />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandle}
              >
                Delete
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandleCancel}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      <Container maxWidth="xl">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} md={12}>
            <CardContent>
              <Box
                component="form"
                sx={{
                  '& .MuiTextField-root': { m: 1, width: '50ch' }
                }}
                noValidate
                autoComplete="off"
              >
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="installation_name"
                        defaultMessage="Installation Name"
                      />
                    }
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="region" defaultMessage="Region" />
                    }
                    name="region"
                    value={formValues.region}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.region === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="location"
                        defaultMessage="Location"
                      />
                    }
                    name="location"
                    value={formValues.location}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.location === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="country" defaultMessage="Country" />
                    }
                    name="country"
                    value={formValues.country}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.country === ''}
                  />
                </div>

                <div>
                  <TextField
                    label={
                      <FormattedMessage id="vpnip" defaultMessage="VPN IP" />
                    }
                    name="vpnIp"
                    value={formValues.vpnIp}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div>
                  <TextField
                    label={
                      <FormattedMessage id="vrmLink" defaultMessage="vrmLink" />
                    }
                    name="vrmLink"
                    value={formValues.vrmLink}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div>
                  <FormControl
                    fullWidth
                    sx={{
                      marginLeft: 1,
                      marginTop: 1,
                      marginBottom: 1,
                      width: 440
                    }}
                  >
                    <InputLabel
                      sx={{
                        fontSize: 14,
                        backgroundColor: 'white'
                      }}
                    >
                      <FormattedMessage
                        id="DeviceType"
                        defaultMessage="Device Type"
                      />
                    </InputLabel>
                    <Select
                      name="device"
                      value={
                        DeviceTypes.indexOf(
                          DeviceTypes[formValues.device - 1]
                        ) + 1
                      }
                      onChange={handleChange}
                    >
                      {DeviceTypes.map((type) => (
                        <MenuItem
                          key={type}
                          value={DeviceTypes.indexOf(type) + 1}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="information"
                        defaultMessage="Information"
                      />
                    }
                    name="information"
                    value={formValues.information}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                {currentUser.userType == UserType.admin && (
                  <>
                    <div>
                      <TextField
                        label="S3 Bucket Name"
                        name="s3writesecretkey"
                        value={
                          formValues.s3BucketId +
                          '-c0436b6a-d276-4cd8-9c44-1eae86cf5d0e'
                        }
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Write Key"
                        name="s3writesecretkey"
                        value={formValues.s3WriteKey}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Write Secret Key"
                        name="s3writesecretkey"
                        value={formValues.s3WriteSecret}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 10
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      marginLeft: '10px'
                    }}
                    disabled={!areRequiredFieldsFilled()}
                  >
                    <FormattedMessage
                      id="applyChanges"
                      defaultMessage="Apply Changes"
                    />
                  </Button>

                  {currentUser.userType == UserType.admin && (
                    <Button
                      variant="contained"
                      onClick={handleDelete}
                      sx={{
                        marginLeft: '10px'
                      }}
                    >
                      <FormattedMessage
                        id="deleteInstallation"
                        defaultMessage="Delete Installation"
                      />
                    </Button>
                  )}

                  {loading && (
                    <CircularProgress
                      sx={{
                        color: theme.palette.primary.main,
                        marginLeft: '20px'
                      }}
                    />
                  )}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="errorOccured"
                        defaultMessage="An error has occurred"
                      />
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setError(false)}
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                  {updated && (
                    <Alert
                      severity="success"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="successfullyUpdated"
                        defaultMessage="Successfully updated"
                      />

                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setUpdated(false)} // Set error state to false on click
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                </div>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default InformationSalidomo;
